import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const STAGE_KEY = 'stage_code';
const SUB_KEY = 'sub';

function findStageIndex(params, stages) {
  const value = params.get(STAGE_KEY);
  if (!value || !stages.length) return 0;
  const idx = stages.findIndex(s => s.code === value);
  return idx >= 0 ? idx : 0;
}

function parseSub(params) {
  return params.get(SUB_KEY) || null;
}

export function usePhaseTransition(stages) {
  const total = stages.length;
  const [searchParams, setSearchParams] = useSearchParams();

  const [phase, setPhase] = useState(() => findStageIndex(searchParams, stages));
  const [sub, setSub] = useState(() => parseSub(searchParams));
  const [isAnimating, setIsAnimating] = useState(false);
  const phaseRef = useRef(phase);
  const subRef = useRef(sub);

  phaseRef.current = phase;
  subRef.current = sub;

  const getIndex = (i) => total ? (i + total) % total : 0;

  const goTo = useCallback((nextPhase) => {
    if (isAnimating || nextPhase === phaseRef.current || !stages.length) return;

    setIsAnimating(true);
    setPhase(nextPhase);
    setSub(null);
    phaseRef.current = nextPhase;
    subRef.current = null;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(STAGE_KEY, stages[nextPhase].code);
      next.delete(SUB_KEY);
      return next;
    });

    setTimeout(() => setIsAnimating(false), 130);
  }, [isAnimating, stages, setSearchParams]);

  const setSubParam = useCallback((value) => {
    setSub(value);
    subRef.current = value;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (stages[phaseRef.current]) {
        next.set(STAGE_KEY, stages[phaseRef.current].code);
      }
      if (value != null) {
        next.set(SUB_KEY, String(value));
      } else {
        next.delete(SUB_KEY);
      }
      return next;
    });
  }, [stages, setSearchParams]);

  useEffect(() => {
    if (isAnimating || !stages.length) return;

    const urlPhase = findStageIndex(searchParams, stages);
    const urlSub = parseSub(searchParams);

    if (urlPhase !== phaseRef.current) {
      setPhase(urlPhase);
      setSub(urlSub);
      phaseRef.current = urlPhase;
      subRef.current = urlSub;
    } else if (urlSub !== subRef.current) {
      setSub(urlSub);
      subRef.current = urlSub;
    }
  }, [searchParams, stages, isAnimating]);

  return { phase, goTo, getIndex, isAnimating, sub, setSubParam };
}

export default usePhaseTransition;
